import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {map, Observable, tap} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StripePaymentService {
  constructor(private tableDataService: DataService) {
  }

  private normalizeStripePayload<T extends Record<string, any>>(payload: T): T {
    const normalized: any = {...payload};

    if (normalized.stripeFlowType === undefined && normalized.entityType !== undefined) {
      normalized.stripeFlowType = normalized.entityType;
    }

    if (normalized.appEntityId === undefined && normalized.entityId !== undefined) {
      normalized.appEntityId = normalized.entityId;
    }

    if (normalized.appTransactionId === undefined && normalized.transactionId !== undefined) {
      normalized.appTransactionId = normalized.transactionId;
    }

    if (normalized.appBankId === undefined && normalized.bankId !== undefined) {
      normalized.appBankId = normalized.bankId;
    }

    if (normalized.AppEntityId === undefined && normalized.appEntityId !== undefined) {
      normalized.AppEntityId = normalized.appEntityId;
    }

    if (normalized.AppTransactionId === undefined && normalized.appTransactionId !== undefined) {
      normalized.AppTransactionId = normalized.appTransactionId;
    }

    if (normalized.AppBankId === undefined && normalized.appBankId !== undefined) {
      normalized.AppBankId = normalized.appBankId;
    }

    delete normalized.entityId;
    delete normalized.entityType;
    delete normalized.transactionId;
    delete normalized.bankId;

    return normalized as T;
  }

  private logStripeReturn(method: 'GET' | 'POST' | 'DELETE', endpoint: string) {
    return tap((result: any) => {
      console.log(`[Stripe API] Return <- ${method} ${endpoint}`, result);
    });
  }

  // getConfig(): Observable<any> {
  //   return this.tableDataService.getData('stripe/config').pipe(map((result: any) => this.unwrapData(result)));
  // }

  ensureCustomer(payload: any): Observable<any> {
    return this.tableDataService
      .post('stripe/customers/ensure', this.normalizeStripePayload(payload || {}))
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/customers/ensure'),
        map((result: any) => this.unwrapData(result))
      );
  }

  createPaymentIntent(payload: any): Observable<any> {
    return this.tableDataService
      .post('stripe/payments/intent', this.normalizeStripePayload(payload || {}))
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/payments/intent'),
        map((result: any) => this.unwrapData(result))
      );
  }
  createSetupIntent(payload: any): Observable<any> {
    return this.tableDataService
      .post('stripe/setup-intent', this.normalizeStripePayload(payload || {}))
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/setup-intent'),
        map((result: any) => this.unwrapData(result))
      );
  }

  verifyApPushMicrodeposits(payload: any): Observable<any> {
    return this.tableDataService
      .post('stripe/rails/ap/push/verify-microdeposits', this.normalizeStripePayload(payload || {}))
      .pipe(
        this.logStripeReturn('POST', '/api/stripe/rails/ap/push/verify-microdeposits'),
        map((result: any) => this.unwrapData(result))
      );
  }

  cancelPaymentIntent(paymentIntentId: string, appEntityId?: string): Observable<any> {
    const payload: any = {};
    if (appEntityId !== undefined && appEntityId !== null && String(appEntityId).trim() !== '') {
      payload.appEntityId = String(appEntityId);
      payload.AppEntityId = String(appEntityId);
    }

    const cancelUrl = `stripe/payments/${encodeURIComponent(paymentIntentId)}/cancel`;
    return this.tableDataService
      .post(cancelUrl, payload)
      .pipe(
        this.logStripeReturn('POST', `/api/${cancelUrl}`),
        map((result: any) => this.unwrapData(result))
      );
  }

  getPaymentStatus(paymentIntentId: string): Observable<any> {
    return this.tableDataService
      .getData('stripe/payments', paymentIntentId)
      .pipe(
        this.logStripeReturn('GET', `/api/stripe/payments/${encodeURIComponent(paymentIntentId)}`),
        map((result: any) => this.unwrapData(result))
      );
  }

  getPaymentMethods(appEntityId: string, stripeFlowType?: string | number | null): Observable<any[]> {
    const params = stripeFlowType !== null && stripeFlowType !== undefined
      ? `appEntityId=${encodeURIComponent(appEntityId)}&stripeFlowType=${encodeURIComponent(String(stripeFlowType))}`
      : `appEntityId=${encodeURIComponent(appEntityId)}`;
    return this.tableDataService
      .getDataWithParams('stripe/payment-methods', params)
      .pipe(map((result: any) => this.unwrapDataArray(result)));
  }
  deleteStripeSetup(appEntityId: string, stripeFlowType?: string | number | null): Observable<any> {
    const entityId = encodeURIComponent(String(appEntityId || '').trim());
    const params =
      stripeFlowType !== null && stripeFlowType !== undefined && String(stripeFlowType).trim() !== ''
        ? `${entityId}?stripeFlowType=${encodeURIComponent(String(stripeFlowType))}`
        : entityId;
    return this.tableDataService
      .deleteDataById('StripeSetup', params)
      .pipe(map((result: any) => this.unwrapData(result)));
  }

  getStripeSetup(appEntityId: string): Observable<any> {
    return this.tableDataService
      .getData('StripeSetup', encodeURIComponent(String(appEntityId || '').trim()))
      .pipe(
        this.logStripeReturn('GET', `/api/StripeSetup/${encodeURIComponent(String(appEntityId || '').trim())}`),
        map((result: any) => result)
      );
  }

  getTransactions(
    appEntityId: string,
    stripeFlowType?: string | number | null,
    page = 1,
    pageSize = 20,
    status?: string | number | null,
    fromDate?: string | null,
    toDate?: string | null,
  ): Observable<any> {
    let params = `appEntityId=${encodeURIComponent(appEntityId)}`
      + `&page=${encodeURIComponent(String(page))}`
      + `&pageSize=${encodeURIComponent(String(pageSize))}`;

    if (stripeFlowType !== null && stripeFlowType !== undefined && String(stripeFlowType).trim() !== '') {
      params += `&stripeFlowType=${encodeURIComponent(String(stripeFlowType))}`;
    }

    if (status !== null && status !== undefined && String(status).trim() !== '' && String(status).trim() !== 'All') {
      params += `&status=${encodeURIComponent(String(status))}`;
    }
    if (fromDate && String(fromDate).trim() !== '') {
      params += `&fromDate=${encodeURIComponent(String(fromDate))}`;
    }
    if (toDate && String(toDate).trim() !== '') {
      params += `&toDate=${encodeURIComponent(String(toDate))}`;
    }

    return this.tableDataService
      .getDataWithParams('stripe/transactions', params)
      .pipe(
        this.logStripeReturn('GET', `/api/stripe/transactions?${params}`),
        map((result: any) => this.unwrapPagedResult(result))
      );
  }
  getAllPayments(
    appBankId: string,
    appEntityId: string,
    stripeFlowType?: string | number | null,
    status: string = 'All',
    page = 1,
    pageSize = 20,
    fromDate?: string | null,
    toDate?: string | null,
  ): Observable<any> {
    let params =
      `appBankId=${encodeURIComponent(appBankId)}` +
      `&appEntityId=${encodeURIComponent(appEntityId)}` +
      `&status=${encodeURIComponent(String(status || ''))}` +
      `&page=${encodeURIComponent(String(page))}` +
      `&pageSize=${encodeURIComponent(String(pageSize))}`;

    if (stripeFlowType !== null && stripeFlowType !== undefined && String(stripeFlowType).trim() !== '') {
      params += `&stripeFlowType=${encodeURIComponent(String(stripeFlowType))}`;
    }
    if (fromDate && String(fromDate).trim() !== '') {
      params += `&fromDate=${encodeURIComponent(String(fromDate))}`;
    }
    if (toDate && String(toDate).trim() !== '') {
      params += `&toDate=${encodeURIComponent(String(toDate))}`;
    }

    return this.tableDataService
      .getDataWithParams('stripe/payments/all', params)
      .pipe(
        this.logStripeReturn('GET', `/api/stripe/payments/all?${params}`),
        map((result: any) => this.unwrapPagedResult(result))
      );
  }

  /**
   * AP Admin: List transactions by entity only (no bank or status filter)
   * Maps to: GET /api/stripe/payments/all?appEntityId=...&fromDate=...&toDate=...&page=...&pageSize=...
   */
  getAllPaymentsByEntity(
    appEntityId: string,
    page = 1,
    pageSize = 20,
    fromDate?: string | null,
    toDate?: string | null,
    status?: string | number | null,
  ): Observable<any> {
    // Ensure AP mode uses Connect/vendor flow type = 2 and full UTC timestamps when only a date is provided
    const expandToStartOfDayUtc = (val: string) => {
      const s = String(val).trim();
      if (!s) {
        return '';
      }
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : s;
    };
    const expandToEndOfDayUtc = (val: string) => {
      const s = String(val).trim();
      if (!s) {
        return '';
      }
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T23:59:59Z` : s;
    };

    let params = `appEntityId=${encodeURIComponent(String(appEntityId))}`
      + `&stripeFlowType=${encodeURIComponent('2')}`
      + `&page=${encodeURIComponent(String(page))}`
      + `&pageSize=${encodeURIComponent(String(pageSize))}`;

    if (fromDate && String(fromDate).trim() !== '') {
      const from = expandToStartOfDayUtc(String(fromDate));
      params += `&fromDate=${encodeURIComponent(from)}`;
    }
    if (toDate && String(toDate).trim() !== '') {
      const to = expandToEndOfDayUtc(String(toDate));
      params += `&toDate=${encodeURIComponent(to)}`;
    }
    // Optional status filtering for AP mode (empty or 'All' means no filter)
    if (status !== null && status !== undefined) {
      const s = String(status).trim();
      if (s !== '' && s.toLowerCase() !== 'all') {
        params += `&status=${encodeURIComponent(s)}`;
      }
    }

    return this.tableDataService
      .getDataWithParams('stripe/payments/all', params)
      .pipe(
        this.logStripeReturn('GET', `/api/stripe/payments/all?${params}`),
        map((result: any) => this.unwrapPagedResult(result))
      );
  }
  private unwrapDataArray<T>(result: any): T[] {
    if (result === null || result === undefined) {
      return [];
    }

    const payload = result.Data !== undefined ? result.Data : result.data !== undefined ? result.data : result;
    if (Array.isArray(payload)) {
      return payload as T[];
    }

    return payload ? [payload as T] : [];
  }

  private unwrapData<T>(result: any): T {
    if (result === null || result === undefined) {
      return result as T;
    }

    const payload = result.Data !== undefined ? result.Data : result.data !== undefined ? result.data : result;

    if (Array.isArray(payload)) {
      if (payload.length === 0) {
        return payload as T;
      }
      return payload[0] as T;
    }

    return payload as T;
  }

  private unwrapPagedResult(result: any): { data: any[]; totalCount: number; page: number; pageSize: number } {
    if (result === null || result === undefined) {
      return {
        data: [],
        totalCount: 0,
        page: 1,
        pageSize: 20,
      };
    }

    const rows = this.unwrapDataArray<any>(result);
    const totalCount = Number(result?.TotalCount ?? result?.totalCount ?? rows.length ?? 0);
    const page = Number(result?.Page ?? result?.page ?? 1);
    const pageSize = Number(result?.PageSize ?? result?.pageSize ?? 20);

    return {
      data: Array.isArray(rows) ? rows : [],
      totalCount: Number.isNaN(totalCount) ? 0 : totalCount,
      page: Number.isNaN(page) || page <= 0 ? 1 : page,
      pageSize: Number.isNaN(pageSize) || pageSize <= 0 ? 20 : pageSize,
    };
  }
}
